import type { ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';

/**
 * Transforms projects/teams with boards into nested Mantine Combobox format
 *
 * @param {ProjectsResponse[]} projects - Array of projects/teams
 * @param {string} searchTerm - Search term to filter results
 * @returns {ComboboxItemGroup<ComboboxItem, string>[]} Formatted data for Mantine Combobox with grouping
 */
const formatBoardsForCombobox = (
  projects: ProjectsResponse[],
  searchTerm = '',
): ComboboxItemGroup<ComboboxItem, string>[] => {
  const groups: ComboboxItemGroup<ComboboxItem, string>[] = [];

  projects.forEach((project) => {
    const filteredBoards = project.subprojects.filter(
      (board) =>
        board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    if (filteredBoards.length > 0) {
      const groupItems: ComboboxItem[] = filteredBoards.map((board) => ({
        value: board.id,
        label: board.name,
      }));

      groups.push({
        group: project.name,
        items: groupItems,
      });
    }
  });

  return groups;
};

/**
 * Gets the default selected board (first board from first team)
 *
 * @param {ProjectsResponse[]} projects - Array of projects/teams
 * @returns {string | undefined} ID of the first board
 */
const getDefaultSelectedBoard = (projects: ProjectsResponse[]): string | undefined => {
  const firstProject = projects[0];

  return firstProject?.subprojects[0]?.id;
};

export { formatBoardsForCombobox, getDefaultSelectedBoard };
